import { documentToHtmlString, type Options } from '@contentful/rich-text-html-renderer';
import { BLOCKS, Document } from '@contentful/rich-text-types';

import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import {
	pricingTiersTableContentType,
	renderPricingTiersTableEmbeddedEntry,
} from '../../../uniform/components/pricing-tiers-table/src/render-pricing-tiers-table-embedded-entry.ts';

export const richTextToHTML = (richText: Document) => {
	let htmlString: string = documentToHtmlString(richText, getRichTextRenderOptions());

	htmlString = htmlString.replace(/\n/g, '<br />');

	return htmlString;
};

const DEFAULT_STRIP_KEYS = new Set(['sys', 'metadata', 'createdAt', 'updatedAt']);

export interface StripContentfulMetadataOptions {
	keys?: Set<string> | string[];
}
export const stripContentfulMetadata = (
	data: unknown,
	options: StripContentfulMetadataOptions = {},
): void => {
	const keysToStrip =
		options.keys !== null && options.keys !== undefined
			? new Set(options.keys)
			: DEFAULT_STRIP_KEYS;

	const walk = (node: unknown): void => {
		if (Array.isArray(node)) {
			node.forEach(walk);
		} else if (node !== null && typeof node === 'object') {
			const obj = node as Record<string, unknown>;
			for (const key of keysToStrip) {
				delete obj[key];
			}
			for (const value of Object.values(obj)) {
				walk(value);
			}
		}
	};

	walk(data);
};

export const getShouldStripExcessData = () => {
	return checkFeatureFlagGate(featureFlags.waePricingStripExcessData);
};

interface EmbeddedEntryRendererArgs {
	fields: unknown;
}

type EmbeddedEntryRenderer = (args: EmbeddedEntryRendererArgs) => string | undefined;

const embeddedEntryRenderers: Record<string, EmbeddedEntryRenderer> = {
	[pricingTiersTableContentType]: renderPricingTiersTableEmbeddedEntry,
};

function getRichTextRenderOptions(): Options | undefined {
	if (!checkFeatureFlagGate(featureFlags.waeEmbeddedPricingTiersTable)) {
		return undefined;
	}

	return {
		renderNode: {
			[BLOCKS.EMBEDDED_ENTRY]: (node) => {
				const target = node.data['target'] as {
					sys?: { contentType?: { sys?: { id?: string } } };
					fields?: unknown;
				};
				const contentTypeId = target?.sys?.contentType?.sys?.id;
				const renderer = contentTypeId ? embeddedEntryRenderers[contentTypeId] : undefined;
				return renderer?.({ fields: target?.fields }) ?? '<div></div>';
			},
		},
	};
}
